import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { homePathForUser, LOGIN_PATH } from './RoleHomeRedirect';
import { errorMessageFor } from './error-message';
import { LogoutButton } from './LogoutButton';
import { Brand } from '../components/Brand';
import { ApiError } from '../api/api-error';

/**
 * `/cambiar-password` (spec: "Alta y contraseña temporal"): the account with
 * a temporary password must change it before reaching any business route.
 * Confirmation match is validated client-side without ever persisting the
 * passwords; strength/length rules stay server-side and surface through the
 * API's own error message. On success the updated UserDTO's
 * `mustChangePassword` is `false`, so the redirect lands on the real home.
 */
export function ChangePasswordPage() {
  const { changePassword, user } = useAuth();
  const navigate = useNavigate();
  const isVoluntaryChange = !user?.mustChangePassword;
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;
    setError('');

    if (newPassword !== confirmPassword) {
      setError('La confirmación no coincide con la nueva contraseña.');
      return;
    }

    setSubmitting(true);
    try {
      const user = await changePassword(currentPassword, newPassword);
      navigate(homePathForUser(user), { replace: true });
    } catch (caught) {
      // A 401 means the session is already gone server-side (e.g. an admin
      // reset this same account from another session while this form was
      // open): there is nothing left to retry here, only a fresh login.
      if (caught instanceof ApiError && caught.status === 401) {
        navigate(LOGIN_PATH, { replace: true });
        return;
      }
      setError(errorMessageFor(caught));
      setSubmitting(false);
    }
  }

  return (
    <main className="gym-entry">
      <div className="gym-entry__panel">
        <Brand tone="light" />
        <h1 className="gym-entry__title">Cambiar contraseña</h1>
        <p className="gym-entry__lead">
          {isVoluntaryChange
            ? 'Elegí una contraseña nueva.'
            : 'Por seguridad tenés que elegir una contraseña nueva antes de continuar.'}
        </p>
        <form className="gym-form" onSubmit={handleSubmit} noValidate>
          {error && (
            <p role="alert" className="gym-form__error">{error}</p>
          )}

          <div className="gym-field">
            <label htmlFor="current-password" className="gym-field__label">Contraseña actual</label>
            <input
              id="current-password"
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              disabled={submitting}
              onChange={(event) => setCurrentPassword(event.target.value)}
              className="gym-input"
            />
          </div>

          <div className="gym-field">
            <label htmlFor="new-password" className="gym-field__label">Contraseña nueva</label>
            <input
              id="new-password"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              required
              value={newPassword}
              disabled={submitting}
              onChange={(event) => setNewPassword(event.target.value)}
              className="gym-input"
            />
          </div>

          <div className="gym-field">
            <label htmlFor="confirm-password" className="gym-field__label">Confirmar contraseña nueva</label>
            <input
              id="confirm-password"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              disabled={submitting}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className="gym-input"
            />
          </div>

          <button type="submit" disabled={submitting} className="gym-button gym-button--primary gym-button--block">
            {submitting ? 'Guardando…' : 'Cambiar contraseña'}
          </button>
        </form>
        <div className="gym-entry__footer">
          {isVoluntaryChange && (
            <Link className="gym-button gym-button--secondary gym-button--block" to={homePathForUser(user)}>Volver</Link>
          )}
          {/* Spec: with a temporary password "sólo se permite /cambiar-password y logout". */}
          <LogoutButton variant="light" />
        </div>
      </div>
    </main>
  );
}
